import type { PostHog } from "posthog-js";

const STORAGE_KEY = "tilprep_cookie_consent_v2";
const ADMIN_EMAIL = "infotilprep@gmail.com";

let posthogInstance: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;

function envEnabled(): boolean {
  // Production builds only.
  if (!import.meta.env.PROD) return false;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  return !!(key && key.startsWith("phc_"));
}

export function hasConsent(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "accepted";
  } catch {
    return false;
  }
}

export async function initPostHog(): Promise<PostHog | null> {
  if (posthogInstance) return posthogInstance;
  if (initPromise) return initPromise;
  if (!envEnabled()) return null;
  if (!hasConsent()) return null;

  initPromise = (async () => {
    const { default: posthog } = await import("posthog-js");
    posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
      api_host:
        (import.meta.env.VITE_POSTHOG_HOST as string) ||
        "https://eu.i.posthog.com",
      capture_pageview: false, // we handle SPA pageviews manually
      capture_pageleave: true,
      autocapture: true,
      persistence: "localStorage+cookie",
      disable_session_recording: true,
      // Pre-configured for when session replay is enabled later:
      session_recording: {
        maskAllInputs: true,
        maskTextSelector:
          "[data-ph-mask], input, textarea, [contenteditable='true']",
      },
    });
    posthogInstance = posthog;
    // Initial pageview after init.
    posthog.capture("$pageview");
    return posthog;
  })();

  return initPromise;
}

export function getPostHog(): PostHog | null {
  return posthogInstance;
}

export function capturePageview(path: string): void {
  if (!posthogInstance) return;
  posthogInstance.capture("$pageview", { $current_url: window.location.href, path });
}

export async function identifyUser(userId: string, email?: string | null): Promise<void> {
  const ph = await initPostHog();
  if (!ph) return;
  if (email && email.toLowerCase() === ADMIN_EMAIL) {
    ph.opt_out_capturing();
    return;
  }
  // Identify by user.id only — no email/PII in person properties.
  ph.identify(userId);
}

export function resetAnalytics(): void {
  if (!posthogInstance) return;
  posthogInstance.reset();
}

export function optOutAnalytics(): void {
  if (!posthogInstance) return;
  posthogInstance.opt_out_capturing();
}